import { z } from 'zod';

/**
 * Referral / affiliate attribution events — the money path for both referrer
 * programs (ADR 0012, superseding 0011).
 *
 * Motivating history: `recordReferralForChurch` fired ONLY from
 * `checkout.session.completed`, but the card-on-trial funnel (live 2026-07-31)
 * starts its subscription via the Stripe API and never emits a checkout
 * session. Every referred self-serve church was stamped and charged, and no
 * referral was ever recorded. #300 added the `invoice.paid` call site and fixed
 * the behaviour — but the reason the bug survived from 07-31 to 08-10 is that
 * NOTHING on this path said anything. Every drop was a bare `return`.
 *
 * That is the ADR 0002 shape exactly, so this domain instruments the failure
 * branches first and the happy path second. The two questions it has to be able
 * to answer, in production, without a database:
 *
 *   1. "An affiliate sent us churches — did we attribute any of them?"
 *      → `referral.attribution_stamped` at signup.
 *   2. "A referred church converted — did the referrer get credited?"
 *      → `referral.conversion_recorded` / `referral.commission_accrued`, and,
 *        when they did NOT, `referral.conversion_skipped` with a reason rather
 *        than silence.
 *
 * This matters more than usual because there is no production evidence the path
 * works: as of 2026-08-11 the `Referral` table has never held a row and not one
 * of 488 churches carries a referral attribution. The affiliate launch will be
 * the first real traffic this code has ever seen.
 */

/**
 * Which webhook produced a conversion attempt. Both are legitimate — they serve
 * different funnels — but they fail differently, so every conversion event
 * carries the trigger rather than making the reader guess from timing.
 */
export const REFERRAL_TRIGGERS = [
  /** Welcome Aboard / consulting / super-admin trial — `checkout.session.completed`. */
  'checkout_session',
  /** Self-serve card-on-trial — first `subscription_cycle` `invoice.paid`. */
  'invoice_cycle',
  /**
   * A subscription that charged immediately with no trial — `subscription_create`
   * `invoice.paid` with a non-zero amount. The re-subscribe case: a referred
   * church trials, cancels, then comes back later and pays straight away. It
   * never emits a `subscription_cycle` invoice, so before this it converted
   * without ever crediting its referrer.
   */
  'invoice_initial',
] as const;

export type ReferralTrigger = (typeof REFERRAL_TRIGGERS)[number];

/**
 * Where a referral code was offered to us. A code that fails to resolve at
 * signup is lost forever — there is no later moment that retries it — so the
 * entry point is worth naming.
 */
export const REFERRAL_STAMP_SOURCES = [
  /** The v2 passwordless signup (`completeOnboarding`). */
  'onboarding',
  /** `createOneChurch` — the legacy/admin church-create path. */
  'church_create',
] as const;

export type ReferralStampSource = (typeof REFERRAL_STAMP_SOURCES)[number];

/**
 * Why a conversion produced no credit. Every one of these was previously a bare
 * `return` inside a try block — indistinguishable from success, and from each
 * other.
 */
export const REFERRAL_SKIP_REASONS = [
  /**
   * The church carries a `referredByCodeId` that no longer resolves to a
   * `ReferralCode` row (deleted code, or a stale FK). The church converted and
   * somebody is owed attribution, but we cannot say who.
   */
  'code_not_found',
  /**
   * The code resolved but names neither a member (`userId`) nor an affiliate
   * (`affiliateId`). Nothing to pay and nobody to tell — a malformed code row.
   */
  'no_payee',
  /**
   * An affiliate-attributed church paid an invoice after its 12-month earning
   * window closed. Expected and permanent for every long-lived referred church
   * — catalogued because "the window closed" and "we dropped it" must not look
   * the same, and because the first of these per church is the moment an
   * affiliate stops earning from it.
   */
  'commission_window_closed',
  /**
   * The invoice carried no money (a $0 trial-opening invoice, a fully
   * discounted cycle). Nothing was collected, so nothing is owed.
   */
  'nothing_collected',
  /**
   * A uniqueness constraint rejected the write, i.e. this credit already
   * exists and the webhook re-fired — `referredChurchId` for a member,
   * `stripeInvoiceId` for an affiliate commission. Expected and benign
   * (an annual renewal re-enters the same branch) — catalogued so that "arrived
   * twice" is visible as itself rather than as silence.
   */
  'already_recorded',
  /**
   * An affiliate-attributed church converted through
   * `checkout.session.completed`, which carries Welcome Aboard and consulting
   * purchases. Those are not subscription revenue and earn no commission; the
   * church's subscription invoices will arrive on their own path.
   */
  'not_subscription_revenue',
] as const;

export type ReferralSkipReason = (typeof REFERRAL_SKIP_REASONS)[number];

const churchId = z.string();
const trigger = z.enum(REFERRAL_TRIGGERS);

export const referralSchemas = {
  /**
   * A referral code arrived at signup and resolved. This is the ONLY positive
   * signal that an affiliate's traffic is being attributed at all — without it,
   * "the affiliate sent nobody" and "we dropped every code they sent" look the
   * same until a conversion months later.
   */
  'referral.attribution_stamped': z.object({
    /** Which program will pay if this church converts. Both settle here. */
    audience: z.enum(['member', 'affiliate']),
    churchId,
    /** The public code as typed/linked, e.g. `pastor-mike-7k2`. Not PII. */
    code: z.string(),
    source: z.enum(REFERRAL_STAMP_SOURCES),
  }),

  /**
   * A referral code arrived at signup and matched nothing, so the church was
   * created with NO attribution. Silent today: `resolveReferrer` returns `{}`
   * and the signup proceeds normally.
   *
   * This is the highest-value event in the domain for the affiliate launch. An
   * affiliate whose code is mistyped, retired, or wrong in their own marketing
   * generates a stream of these and zero conversions, and the two are
   * indistinguishable from the outside.
   */
  'referral.code_unresolved': z.object({
    /**
     * The church that ended up unattributed. Emitted only once that row exists,
     * for two reasons: a signup that throws afterwards (an existing account, a
     * validation error) must not be counted as a dropped referral, and without
     * a churchId every one of these would collapse onto the single synthetic
     * PostHog person `unknown` — see `distinctIdFor`.
     */
    churchId,
    /** The code as supplied. Kept verbatim — the typo IS the diagnostic. */
    code: z.string(),
    source: z.enum(REFERRAL_STAMP_SOURCES),
  }),

  /**
   * A member referral was credited — a `Referral` row now exists. `status` is
   * `INELIGIBLE` when the referrer is over the per-year cap, which is a
   * deliberate non-payout rather than a failure.
   */
  'referral.conversion_recorded': z.object({
    churchId,
    referrerUserId: z.string(),
    rewardAmountCents: z.number().int().nonnegative(),
    status: z.enum(['PENDING_PAYOUT', 'INELIGIBLE']),
    trigger,
  }),

  /**
   * An affiliate earned a commission on a paid invoice — an
   * `AffiliateCommission` row now exists.
   *
   * Emitted per invoice, not per church: a monthly church accrues one of these
   * a month until its 12-month window closes, so a rising count against one
   * church is the program working, not a double credit. `amountCents` is what
   * is owed; `collectedCents` is what the church actually paid, carried so the
   * rate can be audited from the event stream without joining the database.
   */
  'referral.commission_accrued': z.object({
    affiliateId: z.string(),
    amountCents: z.number().int().nonnegative(),
    churchId,
    collectedCents: z.number().int().nonnegative(),
    referralCode: z.string(),
    trigger,
  }),

  /**
   * A commission was voided because its invoice was refunded or charged back —
   * the clawback `/affiliates` promises, enforced.
   */
  'referral.commission_voided': z.object({
    affiliateId: z.string(),
    amountCents: z.number().int().nonnegative(),
    /** The church whose invoice came back — this event groups by it. */
    churchId,
    reason: z.enum(['refunded', 'disputed']),
    stripeInvoiceId: z.string(),
  }),

  /**
   * A refund arrived for a commission that could not be voided.
   *
   * `already_paid` is the expensive one and the reason this event exists: the
   * money is gone, the invoice came back, and recovering it is a human
   * conversation. Without this it would be silent — and silent is how a
   * program overpays for months.
   */
  'referral.commission_void_skipped': z.object({
    affiliateId: z.string(),
    amountCents: z.number().int().nonnegative(),
    churchId,
    reason: z.enum(['already_paid', 'already_void']),
    stripeInvoiceId: z.string(),
  }),

  /** The void write itself threw. Goes to Rollbar too. */
  'referral.commission_void_failed': z.object({
    message: z.string(),
    stripeInvoiceId: z.string(),
  }),

  /** A conversion produced no credit, with the reason named. */
  'referral.conversion_skipped': z.object({
    churchId,
    reason: z.enum(REFERRAL_SKIP_REASONS),
    trigger,
  }),

  /**
   * The credit write threw for a reason that is NOT the benign duplicate. Goes
   * to Rollbar as well; catalogued so the failure is countable next to the
   * successes rather than only readable as an exception.
   */
  'referral.conversion_failed': z.object({
    churchId,
    message: z.string(),
    trigger,
  }),
};
